/**
 * Auto-Grade API Service
 *
 * LAUNCH:C6.P3 — Frontend API client for vault auto-grade endpoints.
 * Follows fetchAPI/API_BASE pattern from defectVisionApi.js.
 */
import { fetchAPI } from './api';

const AG_PREFIX = '/vault/auto-grade';

/**
 * POST /vault/auto-grade/trigger — Trigger auto-grade for a vault item.
 * @param {number} vaultItemId
 * @param {string|null} [imageUrl=null] — existing image URL (for re-grade)
 * @returns {Promise<Object>} { event_id, status }
 */
export async function triggerAutoGrade(vaultItemId, imageUrl = null) {
  const body = { vault_item_id: vaultItemId };
  if (imageUrl) body.image_url = imageUrl;
  return fetchAPI(`${AG_PREFIX}/trigger`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * GET /vault/auto-grade/status/{eventId} — Poll auto-grade status.
 * @param {number} eventId
 * @returns {Promise<Object>} { event_id, status, grade_result? }
 */
export async function getAutoGradeStatus(eventId) {
  return fetchAPI(`${AG_PREFIX}/status/${eventId}`);
}

/**
 * GET /vault/auto-grade/quota — Check current user quota.
 * @returns {Promise<Object>} { can_grade, tier, limit, used, remaining, resets_at }
 */
export async function checkQuota() {
  return fetchAPI(`${AG_PREFIX}/quota`);
}

/**
 * GET /vault/auto-grade/history — Paginated auto-grade history.
 * @param {number} [page=1]
 * @param {number} [perPage=20]
 * @returns {Promise<Object>} { items, total, page, per_page }
 */
export async function getAutoGradeHistory(page = 1, perPage = 20) {
  const params = new URLSearchParams();
  params.set('page', page);
  params.set('per_page', perPage);
  return fetchAPI(`${AG_PREFIX}/history?${params.toString()}`);
}

/**
 * POST /vault/auto-grade/retry/{eventId} — Retry a failed auto-grade.
 * @param {number} eventId
 * @returns {Promise<Object>} { event_id, status }
 */
export async function retryAutoGrade(eventId) {
  return fetchAPI(`${AG_PREFIX}/retry/${eventId}`, {
    method: 'POST',
  });
}

/**
 * POST /vault/items/{itemId}/upload-and-grade — Upload image and trigger auto-grade.
 * @param {number} itemId
 * @param {string} contentType — MIME type of the image
 * @returns {Promise<Object>} { presigned_url, event_id, status }
 */
export async function uploadAndGrade(itemId, contentType) {
  return fetchAPI(`/vault/items/${itemId}/upload-and-grade`, {
    method: 'POST',
    body: JSON.stringify({ content_type: contentType }),
  });
}
